export type VideoProjectMode = 'flow_pilot' | 'manual' | 'agent_guided';
export type VideoAspectRatio = '9:16' | '16:9' | '1:1';
export type VideoProjectStatus = 'draft' | 'in_review' | 'ready_for_render' | 'rendering' | 'approved' | 'rejected';
export type VideoReferenceRole = 'avatar' | 'product' | 'brand_asset' | 'environment' | 'style';
export type GoogleVeoModel = 'veo-3.1' | 'veo-3.1-fast';
export type VideoResolution = '720p' | '1080p' | '4k';
export type VideoAiProvider = 'google' | 'replicate' | 'fal';

/**
 * Conta o número de palavras de uma fala/locução.
 */
export function countDialogueWords(dialogue?: string | null): number {
  const clean = String(dialogue || '').trim();
  if (!clean) return 0;
  return clean.split(/\s+/).filter(Boolean).length;
}

/**
 * Calcula automaticamente a duração ideal da cena em segundos a partir do texto da fala.
 * Considera cadência natural de locução comercial em português (~2,6 palavras/s ou ~15,5 caracteres/s)
 * acrescida de margem de respiro inicial/final (~0,6s), com mínimo de 3s.
 */
export function calculateDialogueDurationSeconds(
  dialogue?: string | null,
  fallbackSeconds: number = 5
): number {
  const clean = String(dialogue || '').trim();
  if (!clean) return Math.max(2, Number(fallbackSeconds) || 5);
  const words = clean.split(/\s+/).filter(Boolean).length;
  const chars = clean.replace(/\s+/g, ' ').length;
  const secByWords = words / 2.6;
  const secByChars = chars / 15.5;
  const estimated = Math.ceil(Math.max(secByWords, secByChars) + 0.6);
  return Math.max(3, Math.min(60, estimated));
}

export interface VideoAudioSettings {
  narration_enabled: boolean;
  voice: string;
  language: string;
  music_style: string;
  sound_fx?: string;
}

export interface VideoReference {
  id?: string;
  project_id?: string;
  role: VideoReferenceRole;
  asset_url: string;
  label: string;
  crop_instructions?: string;
  preservation_notes?: string;
  is_active?: number | boolean;
  created_at?: number;
}

export interface VideoScene {
  id?: string;
  project_id?: string;
  scene_order: number;
  duration_seconds: number;
  scenario: string;
  action: string;
  camera: string;
  lighting: string;
  dialogue: string;
  visual_prompt: string;
  reference_ids?: string[];
  video_url?: string | null;
  status?: 'pending' | 'generating' | 'generated' | 'approved' | 'rejected';
  thumbnail_url?: string | null;
  narration_audio_url?: string | null;
}

export interface VideoVersion {
  id: string;
  project_id: string;
  creative_id: string;
  execution_id?: string | null;
  source_type: 'flow_manual_pilot' | 'runway_gen3' | 'kling_ai' | 'external_import' | 'google_veo' | 'scene_upload' | 'scenes_stitched' | string;
  video_url: string;
  thumbnail_url?: string | null;
  duration_seconds?: number | null;
  aspect_ratio?: string;
  file_size_bytes?: number | null;
  mime_type?: string;
  review_notes?: string | null;
  is_selected: number | boolean;
  created_at: number;
}

export interface SceneGenerationOption extends VideoVersion {
  generationNumber: number;
  sceneOrder: number | null;
}

/**
 * Identifica a qual cena (scene_order) uma versão renderizada pertence a partir de review_notes.
 * Retorna null se for um vídeo consolidado/completo.
 */
export function extractVersionSceneOrder(version?: VideoVersion | null): number | null {
  if (!version) return null;
  if (version.source_type === 'scenes_stitched') return null;
  const notes = String(version.review_notes || '');
  const match = notes.match(/Cena\s*#?(\d+)(?!\d)/i);
  if (match && match[1]) {
    const num = Number(match[1]);
    if (!Number.isNaN(num) && num > 0) return num;
  }
  return null;
}

/**
 * Formata timestamp epoch (segundos ou ms) em horário legível (dd/MM HH:mm).
 */
export function formatVersionTimestamp(createdAt?: number | null): string {
  if (!createdAt) return '';
  const ms = createdAt < 1e12 ? createdAt * 1000 : createdAt;
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Retorna todas as gerações (takes) disponíveis para uma cena específica,
 * numeradas cronologicamente (Take v1, v2, v3...) e ordenadas da mais recente para a mais antiga.
 */
export function getSceneGenerations(
  versions: VideoVersion[] | undefined,
  sceneOrder: number,
  currentSceneVideoUrl?: string | null,
  currentSceneDuration?: number | null
): SceneGenerationOption[] {
  const all = Array.isArray(versions) ? versions : [];
  const matching = all.filter((v) => Boolean(v.video_url) && extractVersionSceneOrder(v) === sceneOrder);

  // Deduplica por video_url mantendo o registro mais recente
  const byUrl = new Map<string, VideoVersion>();
  for (const v of matching) {
    if (!byUrl.has(v.video_url)) {
      byUrl.set(v.video_url, v);
    }
  }

  // Se a cena tiver um video_url ativo que não está listado nas versões dessa cena, garante sua presença
  if (currentSceneVideoUrl && !byUrl.has(currentSceneVideoUrl)) {
    const existingAny = all.find((v) => v.video_url === currentSceneVideoUrl);
    byUrl.set(
      currentSceneVideoUrl,
      existingAny || {
        id: `scene_${sceneOrder}_current`,
        project_id: '',
        creative_id: '',
        source_type: 'google_veo',
        video_url: currentSceneVideoUrl,
        duration_seconds: currentSceneDuration || 5,
        review_notes: `Cena #${sceneOrder} (Take vinculado)`,
        is_selected: 0,
        created_at: Math.floor(Date.now() / 1000),
      }
    );
  }

  const chronological = Array.from(byUrl.values()).sort(
    (a, b) => (Number(a.created_at) || 0) - (Number(b.created_at) || 0)
  );

  const numbered: SceneGenerationOption[] = chronological.map((v, idx) => ({
    ...v,
    generationNumber: idx + 1,
    sceneOrder,
  }));

  return numbered.reverse();
}

export interface VideoProject {
  id: string;
  tenant_id: string;
  campaign_id: string;
  creative_id: string;
  source_asset_url?: string | null;
  source_asset_version?: string;
  mode: VideoProjectMode;
  aspect_ratio: VideoAspectRatio;
  target_duration_seconds: number;
  script_goal?: string | null;
  script_hook?: string | null;
  script_body?: string | null;
  script_cta?: string | null;
  audio_settings: VideoAudioSettings;
  style_notes?: string | null;
  revision_number: number;
  status: VideoProjectStatus;
  selected_version_id?: string | null;
  created_at: number;
  updated_at: number;
  scenes: VideoScene[];
  references: VideoReference[];
  versions: VideoVersion[];
}

export interface FlowPackage {
  package_version: string;
  exported_at: string;
  project_id: string;
  campaign_id: string;
  creative_id: string;
  target_format: {
    aspect_ratio: string;
    total_duration_seconds: number;
  };
  script: {
    goal?: string | null;
    hook?: string | null;
    body?: string | null;
    cta?: string | null;
  };
  audio: VideoAudioSettings;
  style_guidelines?: string | null;
  visual_references: Array<{
    id: string;
    role: string;
    label: string;
    url: string;
    preservation?: string;
  }>;
  scenes: Array<{
    scene_number: number;
    duration_seconds: number;
    scenario: string;
    action: string;
    camera: string;
    lighting: string;
    spoken_dialogue: string;
    recommended_prompt: string;
  }>;
  operator_instructions: string[];
}
